'use client';

import { createContext, useContext, useMemo } from 'react';

import { useStore } from 'utils/hooks/store';
import { getColorFromInitials } from 'components/Avatar/utils';
import { getAvatar } from 'containers/Tiptap/lib/tiptap-collab-utils';

export type User = {
  id: string;
  name: string;
  color: string;
  avatar: string;
};

export type UserContextValue = {
  user: User;
};

export const UserContext = createContext<UserContextValue>({
  user: { color: '', id: '', name: '', avatar: '' },
});

export function UserProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
  const { session } = useStore();

  const user = useMemo<User>(() => {
    const userName = session.user?.name || 'Anonymous';
    const nameParts = userName.trim().toUpperCase().split(' ');
    const firstName = nameParts[0]?.charAt(0) || 'A';
    const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1].charAt(0) : firstName;
    const userColor = getColorFromInitials(firstName, lastName) || '#b616e4';

    return {
      id: session.user?.uuid || 'anonymous',
      name: userName,
      color: userColor,
      avatar: getAvatar(userName),
    };
  }, [session.user?.name, session.user?.uuid]);

  return <UserContext.Provider value={{ user }}>{children}</UserContext.Provider>;
}

export const useUser = (): UserContextValue => useContext(UserContext);
